import {of as observableOf, Observable} from 'rxjs';

import {map} from 'rxjs/operators';
import * as convert from 'xml-js';
import {stripXMLPrefix} from '../processdesigner/ctrl-processdesigner.component';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {ResourceService} from '../../../resource.service';

/**
 * Parser for Directory Service Markup Language
 * @param dsml string The source xml/dsml
 * @param objectType string The objectType to filter
 * @return list of attributes for the given objectType
 */
export function filterAttributesFromDsml(dsml: string, objectType: string): string[] {
  if (dsml == null && objectType == null) return [];
  const obj: any = convert.xml2js(dsml, {
    compact: true,
    alwaysArray: true,
    elementNameFn: stripXMLPrefix,
    attributesKey: '$',
    ignoreDoctype: true,
    ignoreComment: true,
  });

  if (
    obj == null ||
    !Array.isArray(obj.dsml) ||
    obj.dsml.length !== 1 ||
    !Array.isArray(obj.dsml[0]['directory-schema']) ||
    obj.dsml[0]['directory-schema'].length !== 1 ||
    !Array.isArray(obj.dsml[0]['directory-schema'][0].class) ||
    obj.dsml[0]['directory-schema'][0].class.length < 1 ||
    !Array.isArray(obj.dsml[0]['directory-schema'][0]['attribute-type']) ||
    obj.dsml[0]['directory-schema'][0]['attribute-type'].length < 1
  ) {
    console.warn('DSML invalid or empty');
    return [];
  }

  const attributes: string[] = [];
  const attrIds: string[] = [];

  obj.dsml[0]['directory-schema'][0].class.forEach(classNode => {
    if (
      Array.isArray(classNode.name) &&
      classNode.name.length >= 1 &&
      Array.isArray(classNode.attribute) &&
      classNode.attribute.length >= 1 &&
      Array.isArray(classNode.name[0]._text) &&
      classNode.name[0]._text.length >= 1 &&
      classNode.name[0]._text[0] === objectType
    ) {
      classNode.attribute.forEach(attrRefNode => {
        if (
          attrRefNode.$ != null &&
          attrRefNode.$.ref != null &&
          attrRefNode.$.ref.charAt(0) === '#'
        ) {
          attrIds.push(attrRefNode.$.ref.substring(1));
        }
      });
    }
  });

  obj.dsml[0]['directory-schema'][0]['attribute-type'].forEach(attrNode => {
    if (attrNode.$ != null && attrNode.$.id != null) {
      const index = attrIds.indexOf(attrNode.$.id);
      if (
        index !== -1 &&
        Array.isArray(attrNode.name) &&
        attrNode.name.length >= 1 &&
        Array.isArray(attrNode.name[0]._text) &&
        attrNode.name[0]._text.length >= 1
      ) {
        attributes.push(attrNode.name[0]._text[0]);
        attrIds.splice(index, 1);
      }
    }
  });

  return attributes.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

export function getSingleXMLTagValue(obj: any, tag: string) {
  if (
    Array.isArray(obj[tag]) &&
    obj[tag].length === 1 &&
    Array.isArray(obj[tag][0]._text) &&
    obj[tag][0]._text.length === 1
  ) {
    return obj[tag][0]._text[0];
  } else {
    return null;
  }
}

/**
 * Extracts all attributes found in the SyncConfig-Attribute-Inclusion Attribute of the given id
 *
 * @param managementAgentId
 * @param resourceService
 * @param envConfigService
 *
 * @return Observable<string[]> all attributes found in the management agent as Observable
 */
export function parseAttributesFromManagementAgent(
  managementAgentId: string,
  resourceService: ResourceService,
  envConfigService: EnvironmentConfigService,
): Observable<string[]> {
  if (managementAgentId == null) return observableOf(null);
  return resourceService.getResource(managementAgentId).pipe(
    map(res => {
      if (res == null) return null;
      const syncConfigAttrName = envConfigService.getAttributeName('SyncConfigAttributeInclusion');
      if (syncConfigAttrName == null) return null;
      const attr: string = res[syncConfigAttrName];
      if (attr == null) return null;
      const regex = /<attribute>([^<]+)<\/attribute>/g;

      const attributes: string[] = [];

      let arr;
      while ((arr = regex.exec(attr)) !== null) {
        attributes.push(arr[1]);
      }

      attributes.push('dn'); // static attribute
      return attributes.sort();
    }),
  );
}
